import { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router'

import { splitDestination } from './navActive'
import { NAVIGATION, PLATFORM_NAVIGATION, RESELLER_NAVIGATION } from './navigation'

import { Avatar, IconButton } from '@/components/primitives'
import { Icon } from '@/components/primitives/Icon'
import { useSession, useSignOut } from '@/lib/auth/session'

function readTheme(): boolean {
  try {
    const saved = localStorage.getItem('convo:theme')
    return saved
      ? saved === 'dark'
      : window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}
export function Topbar({
  onToggleSidebar,
  onOpenSearch,
}: {
  onToggleSidebar: () => void
  onOpenSearch: () => void
}) {
  const { user } = useSession()
  const signOut = useSignOut()
  const { workspaceSlug } = useParams()
  const { pathname, search } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const [dark, setDark] = useState(readTheme)
  const [signOutError, setSignOutError] = useState('')
  const name =
    (user?.user_metadata?.['full_name'] as string | undefined) ??
    user?.email ??
    'Your account'
  const platform = !workspaceSlug
  const reseller = pathname.startsWith('/reseller')
  const sections = reseller ? RESELLER_NAVIGATION : platform ? PLATFORM_NAVIGATION : NAVIGATION
  const route = platform ? pathname : pathname.replace(`/w/${workspaceSlug}`, '')
  const params = new URLSearchParams(search)

  // The deepest match wins, so /team/departments reads "Departments" rather
  // than "Team", and ?tab=invoices reads "Invoices" rather than "Billing".
  const item = sections
    .flatMap((s) => s.items)
    .filter((i) => i.path === route || (i.path && route.startsWith(`${i.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0]

  // The most specific match wins: '/admin' (no params) matches every admin
  // tab, so taking the first match labelled all of them "Overview".
  const child = item?.children
    ?.map((c) => ({ c, ...splitDestination(c.to) }))
    .filter(({ path, params: want }) => path === route && [...want].every(([k, v]) => params.get(k) === v))
    .sort((a, b) => [...b.params].length - [...a.params].length)[0]?.c ??
    // A detail page under a tabbed section — /admin/resellers/<id> — belongs
    // to the tab of the same name, /admin?tab=resellers.
    item?.children?.find((c) => {
      const section = route.slice(item.path.length + 1).split('/')[0]
      return Boolean(section) && splitDestination(c.to).params.get('tab') === section
    })

  const area = reseller ? 'Reseller' : platform ? 'Platform' : 'Workspace'
  const page = child?.label ?? item?.label ?? area
  const trail = child && item ? item.label : area
  // A dropdown, so it closes on an outside click or Escape. Not a fixed
  // backdrop: the bar's backdrop-filter would confine that to the bar.
  useEffect(() => {
    if (!menuOpen) return
    function onPointerDown(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])
  useEffect(() => {
    document.documentElement.dataset['theme'] = dark ? 'dark' : 'light'
    try {
      localStorage.setItem('convo:theme', dark ? 'dark' : 'light')
    } catch {
      /* Preference only. */
    }
  }, [dark])
  return (
    <header className="workspace-topbar relative z-30">
      <IconButton
        variant="ghost"
        icon={<Icon name="menu" />}
        label="Toggle navigation"
        onClick={onToggleSidebar}
      />
      <div className="topbar-breadcrumb">
        <span className="text-text-tertiary">{trail}</span>
        <Icon name="chevron" size={13} />
        <strong>{page}</strong>
      </div>
      {workspaceSlug && (
        <button
          type="button"
          onClick={onOpenSearch}
          aria-label="Search pages"
          className="topbar-search"
        >
          <Icon name="search" size={17} />
          <span>Search pages…</span>
          <kbd>⌘ K</kbd>
        </button>
      )}
      <div className="topbar-actions">
        <IconButton
          variant="ghost"
          icon={<Icon name={dark ? 'sun' : 'moon'} />}
          label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={() => setDark((v) => !v)}
        />
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            aria-haspopup="true"
            className="account-trigger"
          >
            <Avatar name={name} size="sm" presence="online" />
          </button>
          {menuOpen && (
            <div
              role="group"
              aria-label="Your account"
              className="absolute right-0 top-full z-50 mt-2 w-72 rounded-card border border-border bg-surface-raised p-3 shadow-overlay"
            >
              <div className="flex items-center gap-3 p-1">
                <Avatar name={name} />
                <div className="min-w-0">
                  <p className="truncate font-semibold text-text">{name}</p>
                  <p className="truncate text-compact text-text-secondary">{user?.email}</p>
                </div>
              </div>
              <div className="my-2 border-t border-border" />
              {signOutError && (
                <p role="alert" className="mb-2 px-1 text-caption text-danger">
                  {signOutError}
                </p>
              )}
              <button
                type="button"
                onClick={() => {
                  setSignOutError('')
                  void signOut().catch(() =>
                    setSignOutError('Could not sign out. Please try again.'),
                  )
                }}
                className="flex min-h-[44px] w-full items-center gap-3 rounded-control px-3 text-compact text-danger hover:bg-danger-subtle"
              >
                <Icon name="logout" size={18} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
